import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionsContext';
import MyOutreachReport from '../components/reports/MyOutreachReport';
import axios from 'axios';
import { BarChart3, TrendingUp, PieChart, Target, RefreshCw, Brain, Database, Search as SearchIcon, ScanFace } from 'lucide-react';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    AreaChart, Area, PieChart as RechartsPie, Pie, Cell, Legend, Funnel, FunnelChart, LabelList
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ['#6366f1', '#8b5cf6', '#3b82f6', '#10b981', '#f59e0b', '#ef4444'];

// A pie label that fits a narrow card: long stage names are cut (the table below and the tooltip carry them in full).
export const pieLabel = ({ name, value }) => `${name.length > 12 ? `${name.slice(0, 11)}…` : name}: ${value}`;

export default function Reports() {
    const { getAuthHeader } = useAuth();
    const { contactScope } = usePermissions();
    const assignedOnly = contactScope === 'assigned';
    const [kpis, setKpis] = useState(null);
    const [pipeline, setPipeline] = useState([]);
    const [forecast, setForecast] = useState([]);
    const [loading, setLoading] = useState(true);
    const [permissionNotice, setPermissionNotice] = useState('');

    // Usage state
    const [aiUsage, setAiUsage] = useState(null);
    const [apifyUsage, setApifyUsage] = useState(null);
    const [exaUsage, setExaUsage] = useState(null);
    const [rekUsage, setRekUsage] = useState(null);
    const [usagePeriod, setUsagePeriod] = useState('30d');

    useEffect(() => { loadData(); }, [assignedOnly]);
    useEffect(() => { loadUsage(); }, [usagePeriod]);

    const loadData = async () => {
        setLoading(true);
        try {
            // Reports are always server-scoped; do not send owner_id, because
            // the backend clamps every metric to the caller's effective scope.
            const ownerParam = '';
            setPermissionNotice('');
            const [kRes, pRes, fRes] = await Promise.all([
                axios.get(`${API}/reports/dashboard-kpis${ownerParam}`, { headers: getAuthHeader() }),
                axios.get(`${API}/reports/pipeline-summary${ownerParam}`, { headers: getAuthHeader() }),
                axios.get(`${API}/reports/revenue-forecast${ownerParam}`, { headers: getAuthHeader() }),
            ]);
            setKpis(kRes.data);
            setPipeline(Array.isArray(pRes.data) ? pRes.data : (pRes.data?.data || []));
            setForecast(Array.isArray(fRes.data) ? fRes.data : (fRes.data?.data || []));
        } catch (err) {
            if (err?.response?.status === 403) setPermissionNotice('Some report data is restricted to contacts and deals you can access.');
        } finally { setLoading(false); }
    };

    const loadUsage = async () => {
        try {
            const [aiRes, apifyRes, exaRes, rekRes] = await Promise.all([
                axios.get(`${API}/usage/ai?period=${usagePeriod}`, { headers: getAuthHeader() }).catch(() => null),
                axios.get(`${API}/usage/apify?period=${usagePeriod}`, { headers: getAuthHeader() }).catch(() => null),
                axios.get(`${API}/usage/exa?period=${usagePeriod}`, { headers: getAuthHeader() }).catch(() => null),
                axios.get(`${API}/usage/rekognition?period=${usagePeriod}`, { headers: getAuthHeader() }).catch(() => null),
            ]);
            if (aiRes) setAiUsage(aiRes.data);
            if (apifyRes) setApifyUsage(apifyRes.data);
            if (exaRes) setExaUsage(exaRes.data);
            if (rekRes) setRekUsage(rekRes.data);
        } catch {}
    };

    const formatVal = v => v >= 1e6 ? `$${(v/1e6).toFixed(1)}M` : v >= 1e3 ? `$${(v/1e3).toFixed(0)}K` : `$${v}`;
    const formatCost = v => typeof v === 'number' ? `$${v.toFixed(2)}` : '$0.00';

    // a stage with no deals has no slice: its label would still be drawn, piled on the others at the pie's edge
    const pieData = pipeline.filter(p => p.count > 0).map(p => ({ name: p.label, value: p.count, fill: p.color }));

    if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-4 border-stone-700 border-t-transparent rounded-full animate-spin" /></div>;

    return (
        <div className="space-y-6">
            {/* My Outreach (#888) comes first; the Sales section below is unchanged. */}
            <MyOutreachReport getAuthHeader={getAuthHeader} assignedOnly={assignedOnly} />

            <div className="flex items-center justify-between">
                {assignedOnly && <p className="text-sm font-medium text-stone-600" data-testid="assigned-reports-label">Your deals and activities</p>}
                {permissionNotice && <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800" role="status">{permissionNotice}</div>}
                <p className="text-sm text-stone-500">Pipeline analytics and revenue insights</p>
                <button onClick={loadData} className="flex items-center gap-2 px-3 py-2 text-sm border border-stone-200 rounded-lg hover:bg-stone-50 text-stone-600">
                    <RefreshCw className="w-4 h-4" /> Refresh
                </button>
            </div>

            {/* Summary KPIs */}
            <div className="grid grid-cols-4 gap-4">
                {kpis && [
                    { label: 'Total Pipeline', value: formatVal(kpis.pipeline_value), color: 'text-stone-700' },
                    { label: 'Closed Won', value: formatVal(kpis.won_value), color: 'text-emerald-600' },
                    { label: 'Win Rate', value: `${kpis.win_rate}%`, color: 'text-blue-600' },
                    { label: 'Total Deals', value: kpis.total_deals, color: 'text-violet-600' },
                ].map((m, i) => (
                    <div key={i} className="bg-white rounded-xl border border-stone-200 p-5">
                        <p className={`text-2xl font-bold ${m.color}`} style={{ fontFamily: 'Manrope, sans-serif' }}>{m.value}</p>
                        <p className="text-xs text-stone-500 mt-0.5">{m.label}</p>
                    </div>
                ))}
            </div>

            {/* Pipeline by Stage (Bar) */}
            {/* one column until `xl`: the app sidebar is in the page flow, and two charts in a ~470px card clip their labels */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                <div className="bg-white rounded-xl border border-stone-200 p-6" data-testid="pipeline-bar-chart">
                    <h3 className="font-semibold text-stone-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>Pipeline by Stage (Value)</h3>
                    <ResponsiveContainer width="100%" height={280}>
                        <BarChart data={pipeline} margin={{ left: -8, right: 24, bottom: 8 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                            <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#64748b' }} angle={-20} textAnchor="end" height={80} interval={0} />
                            <YAxis tick={{ fontSize: 11, fill: '#64748b' }} tickFormatter={v => formatVal(v)} />
                            <Tooltip formatter={(val) => [formatVal(val), 'Value']} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                            <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                                {pipeline.map(p => <Cell key={p.stage ?? 'unmapped'} fill={p.color} />)}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>

                <div className="bg-white rounded-xl border border-stone-200 p-6" data-testid="pipeline-pie-chart">
                    <h3 className="font-semibold text-stone-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>Deal Count Distribution</h3>
                    <ResponsiveContainer width="100%" height={280}>
                        <RechartsPie>
                            <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} label={pieLabel} labelLine={false} fontSize={10}>
                                {pieData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                            </Pie>
                            <Tooltip />
                        </RechartsPie>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* Revenue Forecast */}
            <div className="bg-white rounded-xl border border-stone-200 p-6" data-testid="forecast-area-chart">
                <h3 className="font-semibold text-stone-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>Revenue Forecast (Weighted Pipeline)</h3>
                {forecast.length === 0 ? (
                    <div className="text-center py-12 text-stone-400">
                        <TrendingUp className="w-10 h-10 mx-auto mb-3 opacity-50" />
                        <p className="text-sm">No forecast data — add deals with close dates to see forecast</p>
                    </div>
                ) : (
                    <ResponsiveContainer width="100%" height={280}>
                        <AreaChart data={forecast} margin={{ left: -10 }}>
                            <defs>
                                <linearGradient id="grad" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#6366f1" stopOpacity={0.15} />
                                    <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                            <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#64748b' }} />
                            <YAxis tick={{ fontSize: 11, fill: '#64748b' }} tickFormatter={v => formatVal(v)} />
                            <Tooltip formatter={(val) => [formatVal(val), 'Weighted Forecast']} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                            <Area type="monotone" dataKey="forecast" stroke="#6366f1" strokeWidth={2.5} fill="url(#grad)" />
                        </AreaChart>
                    </ResponsiveContainer>
                )}
            </div>

            {/* Pipeline Table */}
            <div className="bg-white rounded-xl border border-stone-200 overflow-hidden" data-testid="pipeline-detail-table">
                <div className="px-6 py-4 border-b border-stone-200">
                    <h3 className="font-semibold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Pipeline Stage Details</h3>
                </div>
                <table className="w-full">
                    <thead>
                        <tr className="bg-stone-50 border-b border-stone-200">
                            {['Stage', 'Deals', 'Total Value', 'Avg Value'].map(h => (
                                <th key={h} className="text-left px-5 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-100">
                        {pipeline.map(stage => (
                            <tr key={stage.stage ?? 'unmapped'} className="hover:bg-stone-50 transition-colors">
                                <td className="px-5 py-4">
                                    <div className="flex items-center gap-2">
                                        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: stage.color }} />
                                        <span className="text-sm font-medium text-stone-900">{stage.label}</span>
                                    </div>
                                </td>
                                <td className="px-5 py-4 text-sm text-stone-700 font-medium">{stage.count}</td>
                                <td className="px-5 py-4 text-sm font-semibold text-stone-900">{formatVal(stage.value)}</td>
                                <td className="px-5 py-4 text-sm text-stone-600">{stage.count > 0 ? formatVal(stage.value / stage.count) : '—'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Period Selector for Usage Sections */}
            <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-stone-500">Usage Period:</span>
                {['7d', '30d', '90d'].map(p => (
                    <button key={p} onClick={() => setUsagePeriod(p)}
                        className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${usagePeriod === p ? 'bg-stone-900 text-white border-stone-900' : 'bg-white text-stone-600 border-stone-200 hover:bg-stone-50'}`}>
                        {p === '7d' ? '7 Days' : p === '30d' ? '30 Days' : '90 Days'}
                    </button>
                ))}
            </div>

            {/* AI Usage */}
            <div className="bg-white rounded-xl border border-stone-200 overflow-hidden" data-testid="ai-usage-section">
                <div className="px-6 py-4 border-b border-stone-200 flex items-center gap-3">
                    <Brain className="w-5 h-5 text-violet-600" />
                    <h3 className="font-semibold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>AI Usage</h3>
                </div>
                {aiUsage && (aiUsage.total_tokens > 0 || aiUsage.per_user?.length > 0) ? (
                    <div className="p-6 space-y-6">
                        {/* Stats row */}
                        <div className="grid grid-cols-3 gap-4">
                            <div className="p-4 rounded-lg bg-violet-50 border border-violet-100">
                                <p className="text-xl font-bold text-violet-700" style={{ fontFamily: 'Manrope, sans-serif' }}>{(aiUsage.total_tokens || 0).toLocaleString()}</p>
                                <p className="text-xs text-violet-500 mt-0.5">Total Tokens</p>
                            </div>
                            <div className="p-4 rounded-lg bg-violet-50 border border-violet-100">
                                <p className="text-xl font-bold text-violet-700" style={{ fontFamily: 'Manrope, sans-serif' }}>{formatCost(aiUsage.total_cost)}</p>
                                <p className="text-xs text-violet-500 mt-0.5">Total Cost</p>
                            </div>
                            <div className="p-4 rounded-lg bg-violet-50 border border-violet-100">
                                <p className="text-xl font-bold text-violet-700" style={{ fontFamily: 'Manrope, sans-serif' }}>{formatCost(aiUsage.avg_cost_per_day)}</p>
                                <p className="text-xs text-violet-500 mt-0.5">Avg Cost/Day</p>
                            </div>
                        </div>

                        {/* Per-user table */}
                        {aiUsage.per_user?.length > 0 && (
                            <div>
                                <h4 className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-2">By User</h4>
                                <table className="w-full">
                                    <thead>
                                        <tr className="bg-stone-50 border-b border-stone-200">
                                            {['User', 'Tokens', 'Cost'].map(h => (
                                                <th key={h} className="text-left px-4 py-2.5 text-xs font-semibold text-stone-500 uppercase tracking-wider">{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-stone-100">
                                        {aiUsage.per_user.map((u, i) => (
                                            <tr key={i} className="hover:bg-stone-50">
                                                <td className="px-4 py-3 text-sm font-medium text-stone-900">{u.name || u.user_id}</td>
                                                <td className="px-4 py-3 text-sm text-stone-700">{(u.tokens || 0).toLocaleString()}</td>
                                                <td className="px-4 py-3 text-sm text-stone-700">{formatCost(u.cost)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* Per-feature table */}
                        {aiUsage.per_feature?.length > 0 && (
                            <div>
                                <h4 className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-2">By Feature</h4>
                                <table className="w-full">
                                    <thead>
                                        <tr className="bg-stone-50 border-b border-stone-200">
                                            {['Feature', 'Tokens', 'Cost'].map(h => (
                                                <th key={h} className="text-left px-4 py-2.5 text-xs font-semibold text-stone-500 uppercase tracking-wider">{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-stone-100">
                                        {aiUsage.per_feature.map((f, i) => (
                                            <tr key={i} className="hover:bg-stone-50">
                                                <td className="px-4 py-3 text-sm font-medium text-stone-900">{f.feature}</td>
                                                <td className="px-4 py-3 text-sm text-stone-700">{(f.tokens || 0).toLocaleString()}</td>
                                                <td className="px-4 py-3 text-sm text-stone-700">{formatCost(f.cost)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="p-12 text-center">
                        <Brain className="w-10 h-10 mx-auto mb-3 text-stone-300" />
                        <p className="text-sm text-stone-400">No AI usage data yet</p>
                    </div>
                )}
            </div>

            {/* Apify / Data Enrichment Usage */}
            <div className="bg-white rounded-xl border border-stone-200 overflow-hidden" data-testid="apify-usage-section">
                <div className="px-6 py-4 border-b border-stone-200 flex items-center gap-3">
                    <Database className="w-5 h-5 text-amber-600" />
                    <h3 className="font-semibold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Data Enrichment (Apify)</h3>
                </div>
                {apifyUsage && (apifyUsage.total_runs > 0 || apifyUsage.per_user?.length > 0) ? (
                    <div className="p-6 space-y-6">
                        {/* Stats row */}
                        <div className="grid grid-cols-2 gap-4">
                            <div className="p-4 rounded-lg bg-amber-50 border border-amber-100">
                                <p className="text-xl font-bold text-amber-700" style={{ fontFamily: 'Manrope, sans-serif' }}>{(apifyUsage.total_runs || 0).toLocaleString()}</p>
                                <p className="text-xs text-amber-500 mt-0.5">Total Runs</p>
                            </div>
                            <div className="p-4 rounded-lg bg-amber-50 border border-amber-100">
                                <p className="text-xl font-bold text-amber-700" style={{ fontFamily: 'Manrope, sans-serif' }}>{formatCost(apifyUsage.total_cost)}</p>
                                <p className="text-xs text-amber-500 mt-0.5">Total Cost</p>
                            </div>
                        </div>

                        {/* Per-user table */}
                        {apifyUsage.per_user?.length > 0 && (
                            <div>
                                <h4 className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-2">By User</h4>
                                <table className="w-full">
                                    <thead>
                                        <tr className="bg-stone-50 border-b border-stone-200">
                                            {['User', 'Runs', 'Cost'].map(h => (
                                                <th key={h} className="text-left px-4 py-2.5 text-xs font-semibold text-stone-500 uppercase tracking-wider">{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-stone-100">
                                        {apifyUsage.per_user.map((u, i) => (
                                            <tr key={i} className="hover:bg-stone-50">
                                                <td className="px-4 py-3 text-sm font-medium text-stone-900">{u.name || u.user_id}</td>
                                                <td className="px-4 py-3 text-sm text-stone-700">{(u.runs || 0).toLocaleString()}</td>
                                                <td className="px-4 py-3 text-sm text-stone-700">{formatCost(u.cost)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* Per-actor table */}
                        {apifyUsage.per_actor?.length > 0 && (
                            <div>
                                <h4 className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-2">By Actor</h4>
                                <table className="w-full">
                                    <thead>
                                        <tr className="bg-stone-50 border-b border-stone-200">
                                            {['Actor', 'Runs', 'Cost'].map(h => (
                                                <th key={h} className="text-left px-4 py-2.5 text-xs font-semibold text-stone-500 uppercase tracking-wider">{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-stone-100">
                                        {apifyUsage.per_actor.map((a, i) => (
                                            <tr key={i} className="hover:bg-stone-50">
                                                <td className="px-4 py-3 text-sm font-medium text-stone-900">{a.actor}</td>
                                                <td className="px-4 py-3 text-sm text-stone-700">{(a.runs || 0).toLocaleString()}</td>
                                                <td className="px-4 py-3 text-sm text-stone-700">{formatCost(a.cost)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="p-12 text-center">
                        <Database className="w-10 h-10 mx-auto mb-3 text-stone-300" />
                        <p className="text-sm text-stone-400">No enrichment usage data yet</p>
                    </div>
                )}
            </div>

            {/* Exa Search Usage */}
            <div className="bg-white rounded-xl border border-stone-200 overflow-hidden" data-testid="exa-usage-section">
                <div className="px-6 py-4 border-b border-stone-200 flex items-center gap-3">
                    <SearchIcon className="w-5 h-5 text-blue-600" />
                    <h3 className="font-semibold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Web Search (Exa)</h3>
                </div>
                {exaUsage && (exaUsage.total_runs > 0) ? (
                    <div className="p-6 space-y-6">
                        {/* Stats row */}
                        <div className="grid grid-cols-2 gap-4">
                            <div className="p-4 rounded-lg bg-blue-50 border border-blue-100">
                                <p className="text-xl font-bold text-blue-700" style={{ fontFamily: 'Manrope, sans-serif' }}>{(exaUsage.total_runs || 0).toLocaleString()}</p>
                                <p className="text-xs text-blue-500 mt-0.5">Total Searches</p>
                            </div>
                            <div className="p-4 rounded-lg bg-blue-50 border border-blue-100">
                                <p className="text-xl font-bold text-blue-700" style={{ fontFamily: 'Manrope, sans-serif' }}>{formatCost(exaUsage.total_cost)}</p>
                                <p className="text-xs text-blue-500 mt-0.5">Total Cost</p>
                            </div>
                        </div>

                        {/* Per-user table */}
                        {exaUsage.by_user?.length > 0 && (
                            <div>
                                <h4 className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-2">By User</h4>
                                <table className="w-full">
                                    <thead>
                                        <tr className="bg-stone-50 border-b border-stone-200">
                                            {['User', 'Searches', 'Cost'].map(h => (
                                                <th key={h} className="text-left px-4 py-2.5 text-xs font-semibold text-stone-500 uppercase tracking-wider">{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-stone-100">
                                        {exaUsage.by_user.map((u, i) => (
                                            <tr key={i} className="hover:bg-stone-50">
                                                <td className="px-4 py-3 text-sm font-medium text-stone-900">{u.user_name || u.user_id}</td>
                                                <td className="px-4 py-3 text-sm text-stone-700">{(u.runs || 0).toLocaleString()}</td>
                                                <td className="px-4 py-3 text-sm text-stone-700">{formatCost(u.cost)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="p-12 text-center">
                        <SearchIcon className="w-10 h-10 mx-auto mb-3 text-stone-300" />
                        <p className="text-sm text-stone-400">No Exa search usage data yet</p>
                    </div>
                )}
            </div>

            {/* Rekognition (Face Matching) Usage */}
            <div className="bg-white rounded-xl border border-stone-200 overflow-hidden" data-testid="rekognition-usage-section">
                <div className="px-6 py-4 border-b border-stone-200 flex items-center gap-3">
                    <ScanFace className="w-5 h-5 text-emerald-600" />
                    <h3 className="font-semibold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Face Matching (Rekognition)</h3>
                </div>
                {rekUsage && (rekUsage.total_calls > 0) ? (
                    <div className="p-6 space-y-6">
                        <div className="grid grid-cols-3 gap-4">
                            <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-100">
                                <p className="text-xl font-bold text-emerald-700" style={{ fontFamily: 'Manrope, sans-serif' }}>{(rekUsage.total_calls || 0).toLocaleString()}</p>
                                <p className="text-xs text-emerald-500 mt-0.5">API Calls</p>
                            </div>
                            <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-100">
                                <p className="text-xl font-bold text-emerald-700" style={{ fontFamily: 'Manrope, sans-serif' }}>{(rekUsage.total_images || 0).toLocaleString()}</p>
                                <p className="text-xs text-emerald-500 mt-0.5">Images Processed</p>
                            </div>
                            <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-100">
                                <p className="text-xl font-bold text-emerald-700" style={{ fontFamily: 'Manrope, sans-serif' }}>{formatCost(rekUsage.total_cost)}</p>
                                <p className="text-xs text-emerald-500 mt-0.5">Total Cost</p>
                            </div>
                        </div>

                        {rekUsage.by_operation?.length > 0 && (
                            <div>
                                <h4 className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-2">By Operation</h4>
                                <table className="w-full">
                                    <thead>
                                        <tr className="bg-stone-50 border-b border-stone-200">
                                            {['Operation', 'Calls', 'Images', 'Cost'].map(h => (
                                                <th key={h} className="text-left px-4 py-2.5 text-xs font-semibold text-stone-500 uppercase tracking-wider">{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-stone-100">
                                        {rekUsage.by_operation.map((op, i) => (
                                            <tr key={i} className="hover:bg-stone-50">
                                                <td className="px-4 py-3 text-sm font-medium text-stone-900">{(op.operation || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</td>
                                                <td className="px-4 py-3 text-sm text-stone-700">{(op.calls || 0).toLocaleString()}</td>
                                                <td className="px-4 py-3 text-sm text-stone-700">{(op.images || 0).toLocaleString()}</td>
                                                <td className="px-4 py-3 text-sm text-stone-700">{formatCost(op.cost)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="p-12 text-center">
                        <ScanFace className="w-10 h-10 mx-auto mb-3 text-stone-300" />
                        <p className="text-sm text-stone-400">No face matching usage data yet</p>
                    </div>
                )}
            </div>
        </div>
    );
}
